import { Package } from "@/domain/entities/package.entity";
import { apiClient } from "@/data/api/axios-client";
import { createApiError } from "@/domain/errors/api.error";
import { PackageListResponseDTO } from "../dto/package.dto";

const MOCK_PACKAGES: Package[] = [
    {
        id: 'pkg-early-bird',
        name: 'Early Bird Promotion',
        description: 'Special rate for bookings made before 7 March 2026. Perfect for quick sessions.',
        price: 100,
        basePrice: 100,
        discount: 10,
        durationMinutes: 15,
        finalPrice: 90,
        isActive: true,
        offers: [
            '1 Photoshoot Slot (15 min)',
            'Unlimited Soft Copy (Google Drive)',
            'Valid for 2-10 pax',
            'RM 10 additional per pax (above 6yo)',
        ],
        imageUrl: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=800&auto=format&fit=crop",
    },
    {
        id: 'pkg-family-frame',
        name: 'Family Frame Package',
        description: 'Includes a high-quality physical print and frame for your family portraits.',
        price: 180,
        basePrice: 180,
        discount: 0,
        durationMinutes: 15,
        finalPrice: 180,
        isActive: true,
        offers: [
            '1 Photoshoot Slot (15 min)',
            'Unlimited Soft Copy (Google Drive)',
            'Photo Frame (12 x 18 inch)',
            'Valid for 2-7 pax',
        ],
        imageUrl: "https://images.unsplash.com/photo-1511895426328-dc8714191300?q=80&w=800&auto=format&fit=crop",
    },
    {
        id: 'pkg-premium-hour',
        name: 'Premium 1-Hour Session',
        description: 'Our most comprehensive session for large groups or extended family gatherings.',
        price: 300,
        basePrice: 300,
        discount: 0,
        durationMinutes: 60,
        finalPrice: 300,
        isActive: true,
        offers: [
            '3 Photoshoot Slots (1 Hour)',
            'Unlimited Soft Copy (Google Drive)',
            'Accommodates up to 15 pax',
            'RM 10 additional per pax',
        ],
        imageUrl: "https://images.unsplash.com/photo-1606103836293-0a063ee20566?q=80&w=800&auto=format&fit=crop",
    },
    {
        id: 'pkg-executive-grand',
        name: 'Executive Grand Frame',
        description: 'The ultimate package featuring our largest premium wall frame.',
        price: 350,
        basePrice: 350,
        discount: 0,
        durationMinutes: 15,
        finalPrice: 350,
        isActive: true,
        offers: [
            '1 Photoshoot Slot (15 min)',
            'Unlimited Soft Copy (Google Drive)',
            'Large Photo Frame (20 x 30 inch)',
            'RM 10 additional per pax',
        ],
        imageUrl: "https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?q=80&w=800&auto=format&fit=crop",
    },
];

interface GetPackagesOptions {
    module?: string;
    useMock?: boolean;
}

/**
 * Repository for package data.
 * Supports both real API and mock data via parameter.
 */
export const packageRepository = {
    getPackages: async (options: GetPackagesOptions = {}): Promise<Package[]> => {
        const { module, useMock = false } = options;

        if (useMock) {
            // Simulate network delay
            await new Promise((resolve) => setTimeout(resolve, 800));
            return MOCK_PACKAGES;
        }

        const endpoint = process.env.NEXT_PUBLIC_PACKAGES_ENDPOINT;

        if (!endpoint) {
            throw createApiError({
                statusCode: 500,
                message: "Packages endpoint is not configured.",
                code: "PACKAGES_ENDPOINT_MISSING",
            });
        }

        const response = await apiClient.get<PackageListResponseDTO>(
            endpoint,
            module ? { params: { module } } : undefined
        );
        return Array.isArray(response.data) ? response.data : [];
    },
};
